import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowUpRight, Calendar, Clock } from "lucide-react";
import { api } from "../lib/api";
import { MetaVerifiedBadge } from "../components/MetaVerifiedBadge";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null); setNotFound(false);
    api.get(`/blog/${slug}`)
      .then(({ data }) => setPost(data))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
        else setNotFound(true);
      });
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} — InstaVibe Blog`;
    const meta = document.querySelector('meta[name="description"]');
    const original = meta?.getAttribute("content");
    meta?.setAttribute("content", post.description || "");
    return () => {
      document.title = "InstaVibe | Instagram DM Automation & Marketing Tools for Business";
      if (original) meta?.setAttribute("content", original);
    };
  }, [post]);

  if (notFound) return <Navigate to="/blog" replace />;
  if (!post) {
    return (
      <div className="min-h-screen bg-bone text-white flex items-center justify-center" data-testid="blog-post-loading">
        <div className="text-white/50 text-sm font-mono">Loading post…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone text-white" data-testid="page-blog-post">
      <header className="fixed top-0 w-full z-50 backdrop-blur-2xl bg-bone/80 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 h-16 sm:h-20 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="blog-post-logo">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl overflow-hidden bg-obsidian shrink-0 flex items-center justify-center">
              <img src="/instavibe-logo.jpg" alt="InstaVibe" className="w-full h-full object-cover scale-[1.35]" />
            </div>
            <div className="font-heading font-black text-xl sm:text-2xl tracking-tighter leading-none truncate">
              InstaVibe<span className="text-klein">.</span>
            </div>
          </Link>
          <Link to="/blog" className="btn-ghost text-xs sm:text-sm whitespace-nowrap" data-testid="blog-post-back">
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">All posts</span>
          </Link>
        </div>
      </header>

      <main className="pt-28 sm:pt-32 pb-20 px-4 sm:px-6 md:px-12 max-w-[760px] mx-auto">
        <div className="overline mb-4">— Blog · Insight</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl md:text-5xl tracking-tighter leading-tight mb-6" data-testid="blog-post-title">
          {post.title}
        </h1>
        <div className="flex flex-wrap items-center gap-4 text-xs text-white/50 mb-10 font-mono">
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {post.date}</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="w-3 h-3" /> {post.read_minutes} min read</span>
        </div>

        <article className="space-y-5 text-base sm:text-lg text-white/80 leading-relaxed">
          {(post.body || []).map((block, idx) => {
            if (block.type === "h2") return <h2 key={idx} className="font-heading font-bold text-xl sm:text-2xl text-white mt-12 mb-1 tracking-tight">{block.text}</h2>;
            if (block.type === "ul") return (
              <ul key={idx} className="list-disc pl-6 space-y-2 marker:text-klein">
                {(block.items || []).map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            );
            return <p key={idx}>{block.text}</p>;
          })}
        </article>

        <div className="surface-card p-6 sm:p-8 mt-14 bg-klein/5 border-klein/20" data-testid="blog-post-cta">
          <div className="overline mb-2">— Ready to try it?</div>
          <h3 className="font-heading font-black text-2xl tracking-tighter mb-2">Start your 14-day free trial.</h3>
          <p className="text-sm text-white/70 mb-5">No credit card. Connect your Instagram business account and ship your first automation in under 10 minutes.</p>
          <Link to="/signup" className="btn-primary w-full sm:w-auto inline-flex justify-center" data-testid="blog-post-cta-signup">
            Start free <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </main>

      <footer className="border-t border-white/10 py-8 sm:py-10 px-4 sm:px-6 md:px-12 max-w-[1400px] mx-auto flex flex-wrap gap-4 sm:gap-6 justify-between items-center text-xs sm:text-sm text-white/60">
        <div>© 2026 InstaVibe. Not affiliated with Meta.</div>
        <MetaVerifiedBadge variant="compact" />
        <div className="flex gap-4 sm:gap-6">
          <Link to="/privacy" className="hover:text-white">Privacy</Link>
          <Link to="/terms" className="hover:text-white">Terms</Link>
          <Link to="/contact" className="hover:text-white">Contact</Link>
        </div>
      </footer>
    </div>
  );
}
